import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, X, ImageDown, GripVertical, Archive, ArchiveRestore } from "lucide-react";
import DeleteAllDialog from "../components/DeleteAllDialog";
import { useDragOrder } from "../lib/useDragOrder";
import { api, adminHeaders } from "../lib/api";
import ModulesEditor from "../components/CurriculumEditor";
import AdminImportExport from "../components/AdminImportExport";
import AdminBooksTable from "../components/AdminBooksTable";
import AdminPicksManager from "../components/AdminPicksManager";
import AdminReaders from "../components/AdminReaders";
import SiteContentEditor from "../components/SiteContentEditor";
import ImageField from "../components/ImageField";
import FocalPointPicker from "../components/FocalPointPicker";
import { useContent } from "../context/ContentContext";

const inputCls = "w-full bg-transparent border border-line px-4 py-3 text-vellum font-sans text-sm focus:border-crimson focus:outline-none placeholder:text-vellum-faint";
const labelCls = "font-mono text-[10px] uppercase tracking-[0.2em] text-vellum-muted mb-2 block";
const MEDIA = ["book", "film", "tv", "youtube", "essay"];

const linksToText = (links) => (links || []).map((l) => `${l.label} | ${l.url}`).join("\n");
const textToLinks = (t) => t.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
  const [label, ...rest] = l.split("|");
  return { label: (label || "").trim(), url: rest.join("|").trim() };
});

export default function Admin() {
  const { refresh: refreshContent } = useContent();
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState("");
  const [tab, setTab] = useState("books");
  const [library, setLibrary] = useState([]);
  const [curriculums, setCurriculums] = useState([]);
  const [editLib, setEditLib] = useState(null);
  const [editCur, setEditCur] = useState(null);
  const [coverBusy, setCoverBusy] = useState(false);
  const [coverBatch, setCoverBatch] = useState(null); // { id, label } — undo target after batch cover update
  const [coverStoreLocal, setCoverStoreLocal] = useState(true); // download covers into the site (durable)
  const [selectedCur, setSelectedCur] = useState(() => new Set());
  const [confirmWipe, setConfirmWipe] = useState(null); // "library" | "curriculums"
  const [deleting, setDeleting] = useState(false);
  const [readerCount, setReaderCount] = useState(null);

  const loadData = useCallback(() => {
    api.get("/library").then((r) => setLibrary(r.data)).catch((e) => console.error("Could not load library", e));
    api.get("/curriculums", { params: { include_archived: true } }).then((r) => setCurriculums(r.data)).catch((e) => console.error("Could not load curriculums", e));
  }, []);

  useEffect(() => {
    api.get("/admin/verify")
      .then(() => { setAuthed(true); loadData(); })
      .catch(() => setAuthed(false));
  }, [loadData]);

  // Deep link from a public page: /admin?tab=…&edit=<id> opens that editor
  const [searchParams, setSearchParams] = useSearchParams();
  const openedRef = useRef(null);
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setTab(t);
  }, [searchParams]);
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId || !authed || openedRef.current === editId) return;
    const it = library.find((x) => x.id === editId);
    const c = curriculums.find((x) => x.id === editId);
    if (!it && !c) return;
    openedRef.current = editId;
    if (it) setEditLib({ ...it, _linksText: linksToText(it.links), _tagsText: (it.tags || []).join(", ") });
    else setEditCur({ ...c, modules: c.modules || [] });
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    setSearchParams(next, { replace: true });
  }, [authed, library, curriculums, searchParams, setSearchParams]);

  const doLogin = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/login", { password });
      setAuthed(true);
      loadData();
      toast.success("Welcome, curator");
    } catch (err) {
      toast.error(err.response?.status === 429 ? err.response.data.detail : "Incorrect password");
    }
  };

  const logout = async () => {
    try { await api.post("/admin/logout"); } catch (e) { console.warn("Admin logout failed (cookie may already be gone)", e); }
    setAuthed(false);
  };

  // ---- Library CRUD ----
  const saveLib = async () => {
    const payload = { ...editLib, year: editLib.year ? parseInt(editLib.year) : null, sort: parseInt(editLib.sort) || 0, links: textToLinks(editLib._linksText || ""), tags: (editLib._tagsText || "").split(",").map((s) => s.trim()).filter(Boolean) };
    delete payload._linksText;
    delete payload._tagsText;
    try {
      if (editLib.id) await api.put(`/library/${editLib.id}`, payload, { headers: adminHeaders() });
      else await api.post("/library", payload, { headers: adminHeaders() });
      toast.success("Saved");
      setEditLib(null); loadData();
    } catch { toast.error("Save failed"); }
  };

  // ---- Auto book-cover lookup ----
  const fetchAllCovers = async () => {
    if (!window.confirm("Replace ALL book & essay covers with fresh results from the internet? You can undo this straight afterwards.")) return;
    setCoverBusy(true);
    try {
      const r = await api.post("/admin/library/fetch-covers", { overwrite: true, store_local: coverStoreLocal }, { headers: adminHeaders() });
      const { scanned = 0, updated = 0, not_found = 0, batch_id } = r.data;
      toast.success(`${updated} cover${updated === 1 ? "" : "s"} updated · ${not_found} not found (of ${scanned})`);
      if (batch_id) setCoverBatch({ id: batch_id, label: `${updated} updated, ${not_found} not found` });
      else setCoverBatch(null);
      loadData();
    } catch { toast.error("Cover lookup failed"); }
    finally { setCoverBusy(false); }
  };
  const undoCovers = async () => {
    if (!coverBatch) return;
    setCoverBusy(true);
    try {
      const r = await api.post("/admin/import/undo", { batch_id: coverBatch.id }, { headers: adminHeaders() });
      toast.success(`Reverted ${r.data.restored || 0} cover${(r.data.restored || 0) === 1 ? "" : "s"}`);
      setCoverBatch(null); loadData();
    } catch { toast.error("Undo failed — it may already be reverted"); setCoverBatch(null); }
    finally { setCoverBusy(false); }
  };
  const fetchOneCover = async () => {
    if (!editLib?.id) return;
    setCoverBusy(true);
    try {
      const r = await api.post(`/admin/library/${editLib.id}/fetch-cover`, { store_local: coverStoreLocal }, { headers: adminHeaders() });
      if (r.data.found) { setEditLib((p) => ({ ...p, cover_url: r.data.cover_url })); toast.success(`Cover found (${r.data.source})`); }
      else toast.error("No cover found online for this title");
    } catch { toast.error("Cover lookup failed"); }
    finally { setCoverBusy(false); }
  };

  // ---- Curriculum CRUD ----
  const saveCur = async () => {
    const payload = { ...editCur, sort: parseInt(editCur.sort) || 0, modules: editCur.modules || [] };
    delete payload._itemsText;
    try {
      if (editCur.id) await api.put(`/curriculums/${editCur.id}`, payload, { headers: adminHeaders() });
      else await api.post("/curriculums", payload, { headers: adminHeaders() });
      toast.success("Saved");
      setEditCur(null); loadData();
    } catch { toast.error("Save failed"); }
  };
  const toggleSel = (setter) => (id) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const bulkDelete = async (kind, payload) => {
    setDeleting(true);
    try {
      const r = await api.post(`/admin/${kind}/delete`, payload, { headers: adminHeaders() });
      toast.success(`Deleted ${r.data.deleted} ${kind === "library" ? "entries" : "curriculums"}`);
      if (kind === "curriculums") setSelectedCur(new Set());
      setConfirmWipe(null);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    } finally {
      setDeleting(false);
    }
  };

  const [showArchived, setShowArchived] = useState(false);
  const liveCurs = useMemo(() => curriculums.filter((c) => !c.archived), [curriculums]);
  const archivedCurs = useMemo(() => curriculums.filter((c) => c.archived), [curriculums]);
  const visibleCurs = showArchived ? curriculums : liveCurs;

  const setArchived = async (ids, archived) => {
    if (ids.length === 0) return;
    try {
      await api.post("/admin/curriculums/archive", { ids, archived }, { headers: adminHeaders() });
      toast.success(archived
        ? `Archived ${ids.length} curriculum${ids.length === 1 ? "" : "s"}`
        : `Restored ${ids.length} curriculum${ids.length === 1 ? "" : "s"}`);
      setSelectedCur(new Set());
      loadData();
    } catch { toast.error(archived ? "Could not archive" : "Could not restore"); }
  };

  const [reordering, setReordering] = useState(false);
  const saveCurOrder = async (ordered) => {
    setReordering(true);
    try {
      await Promise.all(ordered.map((c, i) =>
        api.put(`/curriculums/${c.id}`, { ...c, sort: i, modules: c.modules || [] }, { headers: adminHeaders() })
      ));
      toast.success("Order saved");
      loadData();
    } catch { toast.error("Reorder failed"); }
    finally { setReordering(false); }
  };
  const curOrder = useDragOrder(visibleCurs, saveCurOrder);

  const delCur = async (id) => {
    if (!window.confirm("Delete this curriculum?")) return;
    await api.delete(`/curriculums/${id}`, { headers: adminHeaders() });
    toast.success("Deleted"); loadData();
  };

  // ---- Login screen ----
  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 pt-20" data-testid="admin-login">
        <form onSubmit={doLogin} className="w-full max-w-md border border-line bg-ink-800 p-10">
          <p className="overline text-crimson mb-4">Curator Access</p>
          <h1 className="font-serif text-4xl font-light text-vellum mb-8">The Back Room</h1>
          <label className={labelCls}>Password</label>
          <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" autoFocus />
          <button data-testid="admin-login-btn" type="submit" className="mt-6 w-full font-mono text-xs uppercase tracking-[0.2em] py-4 bg-vellum text-ink hover:bg-crimson hover:text-onaccent transition-colors">Enter</button>
        </form>
      </div>
    );
  }

  // ---- Dashboard ----
  return (
    <div className="min-h-screen pt-24 lg:pt-28 pb-24" data-testid="admin-dashboard">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between border-b border-line pb-6 mb-8">
          <div>
            <p className="overline text-crimson mb-2">Curator Dashboard</p>
            <h1 className="font-serif text-4xl lg:text-5xl font-light text-vellum">Manage the Archive</h1>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="font-mono text-xs uppercase tracking-[0.2em] text-vellum-muted hover:text-crimson transition-colors">Sign out</button>
        </div>

        <div className="flex gap-8 mb-8">
          {[["books", `Library (${library.length})`], ["picks", `Spotlight Picks (${library.filter((i) => i.featured).length})`], ["curriculums", `Curriculums (${liveCurs.length})`], ["readers", readerCount === null ? "Readers" : `Readers (${readerCount})`], ["content", "Site Content"], ["import", "Import / Export"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`font-mono text-xs uppercase tracking-[0.2em] pb-2 border-b ${tab === k ? "text-vellum border-crimson" : "text-vellum-muted border-transparent hover:text-onaccent"}`}>{label}</button>
          ))}
        </div>

        {tab === "import" && <AdminImportExport onDone={loadData} />}

        {tab === "books" && (
          <AdminBooksTable
            library={library}
            onDone={loadData}
            onNew={() => setEditLib({ media_type: "book", title: "", creator: "", year: "", cover_url: "", note: "", featured: false, sort: 0, _linksText: "", tags: [], _tagsText: "", field: "Philosophy", form: "", audience: "" })}
            onEditAll={(it) => setEditLib({ ...it, _linksText: linksToText(it.links), _tagsText: (it.tags || []).join(", ") })}
            onBulkDelete={(ids) => bulkDelete("library", { ids })}
            onDeleteAll={() => setConfirmWipe("library")}
            deleting={deleting}
            storeLocal={coverStoreLocal}
            onStoreLocalChange={setCoverStoreLocal}
            onFetchAllCovers={fetchAllCovers}
            coverBusy={coverBusy}
            coverBatch={coverBatch}
            onUndoCovers={undoCovers}
          />
        )}

        {tab === "picks" && <AdminPicksManager library={library} onDone={loadData} />}

        {tab === "readers" && <AdminReaders onCount={setReaderCount} />}

        {tab === "content" && <SiteContentEditor onSaved={refreshContent} />}

        {tab === "curriculums" && (
          <div>
            <button onClick={() => setEditCur({ title: "", field: "", subtitle: "", image_url: "", description: "", overview: "", sort: 0, modules: [] })}
              data-testid="admin-add-curriculum" className="mb-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 border border-line text-vellum hover:border-crimson hover:text-crimson transition-colors">
              <Plus className="w-4 h-4" /> New Curriculum
            </button>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
              <p className="text-sm text-vellum-muted max-w-xl">Drag a curriculum by its handle to change the order readers see on the Personal Curriculums page. Archiving hides a pathway from the site but keeps its modules, image and readers&apos; progress — and frees up its title for a new one.{reordering ? " Saving…" : ""}</p>
              <button
                onClick={() => setShowArchived((v) => !v)}
                data-testid="admin-cur-show-archived"
                className={`shrink-0 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] px-4 py-2.5 border transition-colors ${showArchived ? "border-brass text-brass" : "border-line text-vellum-muted hover:text-vellum hover:border-vellum-muted"}`}
              >
                <Archive className="w-3.5 h-3.5" /> {showArchived ? "Hide archived" : "Show archived"}{archivedCurs.length ? ` (${archivedCurs.length})` : ""}
              </button>
            </div>
            <div className="mb-4 flex flex-wrap items-center gap-4 justify-between border-t border-line pt-5">
              <label className="inline-flex items-center gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="admin-cur-select-all"
                  checked={visibleCurs.length > 0 && selectedCur.size === visibleCurs.length}
                  onChange={(e) => setSelectedCur(e.target.checked ? new Set(visibleCurs.map((c) => c.id)) : new Set())}
                  className="w-4 h-4 accent-crimson"
                />
                <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-vellum-muted">
                  {selectedCur.size > 0 ? `${selectedCur.size} selected` : "Select all"}
                </span>
              </label>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => setArchived([...selectedCur], true)}
                  disabled={selectedCur.size === 0}
                  data-testid="admin-cur-archive-selected"
                  className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 border border-line text-vellum-muted hover:border-brass hover:text-brass transition-colors disabled:opacity-30"
                >
                  <Archive className="w-4 h-4" /> Archive selected
                </button>
                {[...selectedCur].some((id) => archivedCurs.find((c) => c.id === id)) && (
                  <button
                    onClick={() => setArchived([...selectedCur], false)}
                    data-testid="admin-cur-restore-selected"
                    className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 border border-brass/60 text-brass hover:bg-brass hover:text-ink transition-colors"
                  >
                    <ArchiveRestore className="w-4 h-4" /> Restore selected
                  </button>
                )}
                <button
                  onClick={() => { if (window.confirm(`Archive all ${liveCurs.length} live curriculums? They stay editable and can be restored anytime.`)) setArchived(liveCurs.map((c) => c.id), true); }}
                  disabled={liveCurs.length === 0}
                  data-testid="admin-cur-archive-all"
                  className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 border border-line text-vellum-muted hover:border-brass hover:text-brass transition-colors disabled:opacity-30"
                >
                  <Archive className="w-4 h-4" /> Archive all ({liveCurs.length})
                </button>
                <button
                  onClick={() => bulkDelete("curriculums", { ids: [...selectedCur] })}
                  disabled={selectedCur.size === 0 || deleting}
                  data-testid="admin-cur-delete-selected"
                  className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 border border-line text-vellum-muted hover:border-crimson hover:text-crimson transition-colors disabled:opacity-30"
                >
                  <Trash2 className="w-4 h-4" /> Delete selected
                </button>
                <button
                  onClick={() => setConfirmWipe("curriculums")}
                  disabled={curriculums.length === 0 || deleting}
                  data-testid="admin-cur-delete-all"
                  className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 border border-crimson/60 text-crimson hover:bg-crimson hover:text-onaccent transition-colors disabled:opacity-30"
                >
                  <Trash2 className="w-4 h-4" /> Delete all ({curriculums.length})
                </button>
              </div>
            </div>
            <div className="border border-line divide-y divide-line select-none">
              {curOrder.rows.map((c, i) => (
                <div
                  key={c.id}
                  data-testid={`admin-cur-row-${c.id}`}
                  {...curOrder.rowProps(i)}
                  className={`grid grid-cols-[auto_auto_1fr_auto] gap-4 px-5 py-4 items-center transition-colors ${c.archived ? "opacity-60" : ""} ${curOrder.sourceIndex === i ? "opacity-50" : "hover:bg-ink-800"} ${curOrder.targetIndex === i && curOrder.sourceIndex !== i ? "bg-crimson/10 outline outline-1 outline-crimson" : ""}`}
                >
                  <button
                    type="button"
                    {...curOrder.handleProps(i)}
                    data-testid={`admin-cur-handle-${c.id}`}
                    aria-label={`Drag to reorder ${c.title}`}
                    title="Drag to reorder"
                    className="cursor-grab active:cursor-grabbing text-vellum-muted hover:text-brass transition-colors"
                  >
                    <GripVertical className="w-4 h-4" />
                  </button>
                  <input
                    type="checkbox"
                    checked={selectedCur.has(c.id)}
                    onChange={() => toggleSel(setSelectedCur)(c.id)}
                    data-testid={`admin-cur-select-${c.id}`}
                    aria-label={`Select ${c.title}`}
                    className="w-4 h-4 accent-crimson"
                  />
                  <div className="min-w-0">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-crimson">{c.field}</span>
                    {c.archived && <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.2em] text-brass border border-brass/50 px-2 py-0.5" data-testid={`admin-cur-archived-badge-${c.id}`}>Archived</span>}
                    <p className="font-serif text-xl text-vellum truncate">{c.title}</p>
                    <p className="font-mono text-xs text-vellum-faint">{c.modules?.length || 0} modules · {(c.modules || []).reduce((n, m) => n + (m.steps?.length || 0), 0)} steps</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setArchived([c.id], !c.archived)}
                      data-testid={`admin-archive-cur-${c.id}`}
                      aria-label={c.archived ? `Restore ${c.title}` : `Archive ${c.title}`}
                      title={c.archived ? "Restore to the site" : "Archive (hide from the site, keep everything)"}
                      className="text-vellum-muted hover:text-brass transition-colors"
                    >
                      {c.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                    </button>
                    <button onClick={() => setEditCur({ ...c, modules: c.modules || [] })} data-testid={`admin-edit-cur-${c.id}`} className="text-vellum-muted hover:text-onaccent transition-colors"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => delCur(c.id)} data-testid={`admin-del-cur-${c.id}`} className="text-vellum-muted hover:text-crimson transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {confirmWipe && (
        <DeleteAllDialog
          label={confirmWipe === "library" ? "library entries" : "curriculums"}
          count={confirmWipe === "library" ? library.length : curriculums.length}
          busy={deleting}
          testId={confirmWipe === "library" ? "wipe-library" : "wipe-curriculums"}
          onCancel={() => setConfirmWipe(null)}
          onConfirm={(confirm) => bulkDelete(confirmWipe, { all: true, confirm })}
        />
      )}

      {/* Library editor */}
      {editLib && (
        <div className="fixed inset-0 z-[70] bg-ink/90 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-16 px-6" data-lenis-prevent onClick={() => setEditLib(null)}>
          <div className="w-full max-w-2xl bg-ink-800 border border-line p-8" onClick={(e) => e.stopPropagation()} data-testid="admin-lib-editor">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-3xl text-vellum">{editLib.id ? "Edit Entry" : "New Entry"}</h2>
              <button onClick={() => setEditLib(null)}><X className="w-5 h-5 text-vellum-muted hover:text-onaccent" /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelCls}>Medium</label>
                <select value={editLib.media_type} onChange={(e) => setEditLib({ ...editLib, media_type: e.target.value })} className={inputCls} data-testid="lib-media-type">
                  {MEDIA.map((m) => <option key={m} value={m} className="bg-ink">{m}</option>)}
                </select>
              </div>
              <div><label className={labelCls}>Year</label><input value={editLib.year || ""} onChange={(e) => setEditLib({ ...editLib, year: e.target.value })} className={inputCls} /></div>
              <div className="col-span-2"><label className={labelCls}>Title</label><input value={editLib.title} onChange={(e) => setEditLib({ ...editLib, title: e.target.value })} className={inputCls} data-testid="lib-title" /></div>
              <div className="col-span-2"><label className={labelCls}>Creator</label><input value={editLib.creator} onChange={(e) => setEditLib({ ...editLib, creator: e.target.value })} className={inputCls} /></div>
              <div className="col-span-2"><label className={labelCls}>Field of study</label>
                <select value={editLib.field || ""} onChange={(e) => setEditLib({ ...editLib, field: e.target.value })} className={inputCls} data-testid="lib-field">
                  {["Philosophy", "Literature", "Science", "Mathematics", "Art & Film", "History & Society"].map((f) => <option key={f} value={f} className="bg-ink">{f}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-vellum-muted">Cover image</label>
                  {editLib.id && (editLib.media_type === "book" || editLib.media_type === "essay") && (
                    <button type="button" onClick={fetchOneCover} disabled={coverBusy} data-testid="lib-fetch-cover" className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-crimson hover:text-vellum transition-colors disabled:opacity-40">
                      <ImageDown className="w-3 h-3" /> {coverBusy ? "Searching…" : "Find cover online"}
                    </button>
                  )}
                </div>
                <ImageField value={editLib.cover_url} onChange={(v) => setEditLib({ ...editLib, cover_url: v })} testid="lib-cover" />
                <FocalPointPicker image={editLib.cover_url} value={editLib.cover_focal} onChange={(v) => setEditLib({ ...editLib, cover_focal: v })} testid="lib-cover-focal" />
              </div>
              <div className="col-span-2"><label className={labelCls}>Note</label><textarea value={editLib.note} onChange={(e) => setEditLib({ ...editLib, note: e.target.value })} rows={3} className={inputCls} /></div>
              <div className="col-span-2"><label className={labelCls}>Why you should read this</label><input value={editLib.audience || ""} onChange={(e) => setEditLib({ ...editLib, audience: e.target.value })} className={inputCls} placeholder="Because it…" data-testid="lib-audience" /></div>
              <div className="col-span-2"><label className={labelCls}>Links (one per line: Label | URL)</label><textarea value={editLib._linksText} onChange={(e) => setEditLib({ ...editLib, _linksText: e.target.value })} rows={2} className={inputCls} placeholder="Buy | https://…" /></div>
              <div className="col-span-2"><label className={labelCls}>Themes / tags (comma separated)</label><input value={editLib._tagsText} onChange={(e) => setEditLib({ ...editLib, _tagsText: e.target.value })} className={inputCls} placeholder="Stoicism, Philosophy" data-testid="lib-tags" /></div>
              <div><label className={labelCls}>Form</label>
                <select value={editLib.form || ""} onChange={(e) => setEditLib({ ...editLib, form: e.target.value })} className={inputCls} data-testid="lib-form">
                  <option value="" className="bg-ink">— Unspecified —</option>
                  <option value="Fiction" className="bg-ink">Fiction</option>
                  <option value="Non-fiction" className="bg-ink">Non-fiction</option>
                </select>
              </div>
              <div><label className={labelCls}>Sort</label><input value={editLib.sort} onChange={(e) => setEditLib({ ...editLib, sort: e.target.value })} className={inputCls} /></div>
              <div className="flex items-end pb-3"><label className="flex items-center gap-3 cursor-pointer"><input type="checkbox" checked={!!editLib.featured} onChange={(e) => setEditLib({ ...editLib, featured: e.target.checked })} className="w-4 h-4 accent-crimson" /><span className="font-mono text-xs uppercase tracking-[0.2em] text-vellum-muted">Pick of the Month</span></label></div>
            </div>
            <button onClick={saveLib} data-testid="lib-save" className="mt-8 w-full font-mono text-xs uppercase tracking-[0.2em] py-4 bg-vellum text-ink hover:bg-crimson hover:text-onaccent transition-colors">Save Entry</button>
          </div>
        </div>
      )}

      {/* Curriculum editor */}
      {editCur && (
        <div className="fixed inset-0 z-[70] bg-ink/90 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-16 px-6" data-lenis-prevent onClick={() => setEditCur(null)}>
          <div className="w-full max-w-4xl bg-ink-800 border border-line p-8" onClick={(e) => e.stopPropagation()} data-testid="admin-cur-editor">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-3xl text-vellum">{editCur.id ? "Edit Curriculum" : "New Curriculum"}</h2>
              <button onClick={() => setEditCur(null)}><X className="w-5 h-5 text-vellum-muted hover:text-onaccent" /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelCls}>Title</label><input value={editCur.title} onChange={(e) => setEditCur({ ...editCur, title: e.target.value })} className={inputCls} data-testid="cur-title" /></div>
              <div><label className={labelCls}>Field</label><input value={editCur.field} onChange={(e) => setEditCur({ ...editCur, field: e.target.value })} className={inputCls} /></div>
              <div className="col-span-2"><label className={labelCls}>Subtitle</label><input value={editCur.subtitle} onChange={(e) => setEditCur({ ...editCur, subtitle: e.target.value })} className={inputCls} /></div>
              <div className="col-span-2"><label className={labelCls}>Cover image</label>
                <ImageField value={editCur.image_url} onChange={(v) => setEditCur({ ...editCur, image_url: v })} testid="cur-image" aspect="wide" />
                <FocalPointPicker image={editCur.image_url} value={editCur.image_focal} onChange={(v) => setEditCur({ ...editCur, image_focal: v })} testid="cur-image-focal" />
              </div>
              <div className="col-span-2"><label className={labelCls}>Description</label><textarea value={editCur.description} onChange={(e) => setEditCur({ ...editCur, description: e.target.value })} rows={3} className={inputCls} /></div>
              <div className="col-span-2"><label className={labelCls}>Overview (pathway — use → between modules)</label><input value={editCur.overview || ""} onChange={(e) => setEditCur({ ...editCur, overview: e.target.value })} className={inputCls} placeholder="Ancient → Medieval → Modern" data-testid="cur-overview" /></div>
              <div><label className={labelCls}>Sort</label><input value={editCur.sort} onChange={(e) => setEditCur({ ...editCur, sort: e.target.value })} className={inputCls} /></div>
            </div>
            <div className="mt-6 border-t border-line pt-6">
              <ModulesEditor initial={editCur.modules} onChange={(modules) => setEditCur((prev) => ({ ...prev, modules }))} />
            </div>
            <button onClick={saveCur} data-testid="cur-save" className="mt-8 w-full font-mono text-xs uppercase tracking-[0.2em] py-4 bg-vellum text-ink hover:bg-crimson hover:text-onaccent transition-colors">Save Curriculum</button>
          </div>
        </div>
      )}
    </div>
  );
}
